import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import EditIcon from '@mui/icons-material/Edit';
import { Breakpoint, ButtonBase, Stack, Typography, darken } from '@mui/material';
import _ from 'lodash';
import React from 'react';
import { Shadows } from 'theme';
import { makeStyles } from 'tss-react/mui';

type Props = {
  stepNumber: number;
  stepName: string;
  expanded: boolean;

  variant?: 'success' | 'edit';
  disabled?: boolean;

  flexBasis?: string;
  breakpoint?: Breakpoint;

  children?: React.ReactNode;

  toggle?(): void;
};

const PaiStepCollapsed: React.FC<Omit<Props, 'expanded'>> = ({
  stepNumber,
  stepName,
  variant = 'success',
  disabled,
  flexBasis = '100%',
  breakpoint = 'lg',
  toggle,
}) => {
  const { classes, cx } = useStyles({ flexBasis, breakpoint });

  return (
    <ButtonBase
      className={cx(classes.collapsedStep, !_.isNil(toggle) ? 'clickable' : undefined, disabled ? 'disabled' : undefined)}
      onClick={!_.isNil(toggle) ? toggle : undefined}
    >
      {variant === 'edit' ? <EditIcon className={cx(classes.icon, 'edit')} /> : <CheckCircleIcon className={cx(classes.icon, 'success')} />}
      <div className={classes.container}>
        <Stack gap={2} alignItems={'center'} justifyContent={'flex-start'}>
          <Typography className={classes.text}>{`${stepNumber}. ${stepName}`}</Typography>
        </Stack>
      </div>
    </ButtonBase>
  );
};

const PaiStep: React.FC<Omit<Props, 'expanded' | 'variant' | 'disabled'>> = ({
  stepNumber,
  stepName,
  flexBasis = '100%',
  breakpoint = 'lg',
  children,
}) => {
  const { classes, cx } = useStyles({ flexBasis, breakpoint });

  return (
    <div className={classes.step}>
      <div className={classes.title}>
        <Typography className={cx(classes.titleText)}>{`${stepNumber}. ${stepName}`}</Typography>
      </div>
      {children}
    </div>
  );
};

const PaiStepCollapsible: React.FC<Props> = ({ stepNumber, stepName, expanded, variant, disabled, flexBasis, breakpoint, toggle, children }) => {
  return (
    <>
      {expanded ? (
        <PaiStep stepNumber={stepNumber} stepName={stepName} flexBasis={flexBasis} breakpoint={breakpoint} toggle={toggle}>
          {children}
        </PaiStep>
      ) : (
        <PaiStepCollapsed
          stepNumber={stepNumber}
          stepName={stepName}
          variant={variant}
          disabled={disabled}
          flexBasis={flexBasis}
          breakpoint={breakpoint}
          toggle={toggle}
        />
      )}
    </>
  );
};

const useStyles = makeStyles<{ flexBasis: string; breakpoint: Breakpoint }, 'container'>()((theme, { flexBasis, breakpoint }, classes) => ({
  step: {
    flexBasis: flexBasis,
    position: 'relative',
    background: '#FFF',
    boxShadow: Shadows.GREY_SHADOW_LIGHT,
    padding: '16px 8px',
    height: '100%',
    [theme.breakpoints.down(breakpoint)]: {
      flexBasis: '100%',
    },
  },
  title: {
    marginBottom: '16px',
    position: 'relative',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
  },
  titleText: {
    fontSize: theme.typography.pxToRem(13),
    fontWeight: 'bold',
    textAlign: 'center',
    textTransform: 'uppercase',
    '&.highlight': {
      color: theme.palette.primary.main,
    },
  },
  collapsedStep: {
    position: 'relative',
    background: '#FFF',
    boxShadow: Shadows.GREY_SHADOW_LIGHT,
    padding: 'calc(16px + 19.5px + 16px) 8px 16px 8px',
    height: '100%',
    width: '75px',
    '&.clickable': {
      cursor: 'pointer',
      '&:hover': {
        background: darken('#FFF', 0.05),
        [`& .${classes.container}`]: {
          border: `2px solid ${darken('#FFF', 0.05)}`,
        },
      },
    },
    '&.disabled': {
      pointerEvents: 'none',
      color: theme.palette.greys.light,
      '& svg': {
        color: theme.palette.greys.light,
      },
      [`& .${classes.container}`]: {
        border: `2px solid ${theme.palette.greys.lighter}`,
      },
    },
    [theme.breakpoints.down(breakpoint)]: {
      width: '100%',
      height: '75px',
      padding: '8px 16px 8px calc(16px + 19.5px + 16px)',
    },
  },
  icon: {
    position: 'absolute',
    top: 'calc((calc(16px + 19.5px + 16px) - 32px) / 2)',
    left: 'calc(50% - 32px / 2)',
    fontSize: theme.typography.pxToRem(32),
    '&.success': {
      color: theme.palette.success.main,
    },
    '&.edit': {
      color: theme.palette.primary.main,
    },
    [theme.breakpoints.down(breakpoint)]: {
      left: 'calc((calc(16px + 19.5px + 16px) - 32px) / 2)',
      top: 'calc(50% - 32px / 2)',
    },
  },
  container: {
    height: '100%',
    width: '100%',
    border: `2px solid ${theme.palette.backgrounds.main}`,
    paddingTop: '16px',
    [theme.breakpoints.down(breakpoint)]: {
      paddingTop: 0,
      display: 'flex',
      flexDirection: 'column',
      justifyContent: 'center',
    },
  },
  text: {
    fontSize: theme.typography.pxToRem(15),
    fontWeight: 'bold',
    textTransform: 'uppercase',
    whiteSpace: 'nowrap',
    writingMode: 'vertical-lr',
    textOrientation: 'sideways',
    transform: 'rotate(180deg)',
    [theme.breakpoints.down(breakpoint)]: {
      fontSize: theme.typography.pxToRem(13),
      paddingRight: 'calc(40.5px / 2)',
      writingMode: 'unset',
      textOrientation: 'unset',
      transform: 'unset',
    },
  },
}));

export default PaiStepCollapsible;
